/**
 * The `PASSWORD_CHANGE` step — a password change forced mid-sign-in, authorised by the step
 * token rather than by a session (there is no session yet).
 *
 * ── The rules are the server's, and are not restated here ────────────────────
 *
 * This screen does NOT hardcode "12 characters, a capital, a digit, a special character".
 * It renders the `message` the step arrived with, which is the server's own sentence, and
 * on rejection it renders the server's `issues[]` verbatim. Two copies of a password policy
 * is the same defect class as two copies of a payload shape — the copy in the UI drifts,
 * and then the form cheerfully accepts something the API then refuses, or refuses something
 * the API would have taken.
 *
 * Both rejection shapes were exercised live and both are handled:
 *
 *   422 SECURITY_VALIDATION   `error.issues[]`, four separate complaints about the same
 *                             field, each already specific ("Password must contain at least
 *                             one capital letter."). Joined onto `newPassword`.
 *   400 AUTH_PASSWORD_POLICY  flat, no issues — the similarity and reuse checks, e.g.
 *                             'That password contains "stepflow", which is part of your
 *                             name…'. Bound to the field by `describeAuthFailure`.
 *
 * The only check made locally is that the two entries match, because the server never sees
 * the confirmation field and cannot make it.
 */
import { useState, type FormEvent } from 'react';
import { KeyRound } from 'lucide-react';

import { api } from '@frontend/api/client';
import { Button } from '@frontend/components/ui/Button';
import { FieldShell, Input } from '@frontend/components/ui/Field';
import { Banner } from '@frontend/components/ui/Page';

import { describeAuthFailure } from './authErrors';
import { readLoginOutcome, unrecognisedOutcome, type LoginOutcome } from './loginSteps';

interface Props {
  stepToken: string;
  message: string;
  onAdvance: (outcome: LoginOutcome) => void;
  onRestart: (reason: string) => void;
}

const MISMATCH =
  'The two passwords are not the same. Retype the confirmation — it must match the new password exactly, ' +
  'including capitals.';

export function PasswordChange({ stepToken, message, onAdvance, onRestart }: Props): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    const form = new FormData(event.currentTarget);
    const newPassword = String(form.get('newPassword') ?? '');
    const confirmation = String(form.get('confirmPassword') ?? '');

    if (newPassword !== confirmation) {
      setFieldErrors({ confirmPassword: MISMATCH });
      setError(MISMATCH);
      return;
    }

    setBusy(true);
    try {
      const body = await api.post<unknown>('/auth/password/change', { stepToken, newPassword });
      const outcome = readLoginOutcome(body);
      if (outcome === null) {
        setError(unrecognisedOutcome('POST /api/auth/password/change'));
        return;
      }
      onAdvance(outcome);
    } catch (caught) {
      const failure = describeAuthFailure(
        caught,
        'PASSWORD_CHANGE',
        'The password could not be changed and no reason was returned. Check your connection and try again.',
      );
      if (failure.restart) {
        onRestart(failure.message);
        return;
      }
      setError(failure.message);
      setFieldErrors(failure.fieldErrors);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} data-testid="password-change-form">
      <h1 className="mb-1 text-section font-semibold text-content">Choose a new password</h1>
      {/* The server's wording, including its statement of the policy. */}
      <p className="mb-4 text-row text-content-secondary" data-testid="step-message">
        {message}
      </p>

      {error !== null && (
        <Banner tone="error" className="mb-4" data-testid="login-error">
          {error}
        </Banner>
      )}

      <div className="space-y-3">
        <FieldShell
          label="New password"
          htmlFor="new-password"
          required
          fieldKey="newPassword"
          error={fieldErrors['newPassword'] ?? null}
        >
          <Input
            id="new-password"
            name="newPassword"
            rawName
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            invalid={fieldErrors['newPassword'] !== undefined}
            data-testid="input-new-password"
          />
        </FieldShell>
        <FieldShell
          label="Confirm new password"
          htmlFor="confirm-password"
          required
          fieldKey="confirmPassword"
          error={fieldErrors['confirmPassword'] ?? null}
        >
          <Input
            id="confirm-password"
            name="confirmPassword"
            rawName
            type="password"
            autoComplete="new-password"
            required
            invalid={fieldErrors['confirmPassword'] !== undefined}
            data-testid="input-confirm-password"
          />
        </FieldShell>
      </div>

      <Button
        type="submit"
        variant="primary"
        fullWidth
        className="mt-4"
        isLoading={busy}
        leadingIcon={<KeyRound aria-hidden className="h-3.5 w-3.5" />}
        data-testid="submit-password-change"
      >
        Set password and continue
      </Button>
      <p className="mt-2 text-body text-content-secondary">
        Changing your password signs every other device out.
      </p>
    </form>
  );
}
